import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import IntelResultList from '../Intel/IntelResultList';
import { describeApiError } from './agentMeta';

const TYPE_OPTIONS = [
  { value: '', label: 'Auto-detect' },
  { value: 'ip', label: 'IP address' },
  { value: 'domain', label: 'Domain' },
  { value: 'url', label: 'URL' },
  { value: 'hash', label: 'File hash' },
];

/** Ad-hoc indicator lookup against every enabled threat-intel provider. */
const IndicatorLookup = () => {
  const [type, setType] = useState('');
  const [value, setValue] = useState('');
  const [bypassCache, setBypassCache] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    const v = value.trim();
    if (!v) return;
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.enrichment.lookup(v, { type: type || undefined, refresh: bypassCache });
      setResult(res.data);
    } catch (err) {
      setResult(null);
      setError(describeApiError(err, 'Lookup failed').message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>Indicator lookup</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Query every enabled threat-intelligence provider for an IP, domain, URL or file hash.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Type</InputLabel>
            <Select value={type} label="Type" onChange={(e) => setType(e.target.value)}>
              {TYPE_OPTIONS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
            sx={{ flexGrow: 1, minWidth: 260 }}
            inputProps={{ maxLength: 2048 }}
          />
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={run}
            disabled={loading || !value.trim()}
          >
            Look up
          </Button>
        </Box>
        <FormControlLabel
          sx={{ mt: 0.5 }}
          control={<Checkbox size="small" checked={bypassCache} onChange={(e) => setBypassCache(e.target.checked)} />}
          label="Bypass cache"
        />
        {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {result && result.skipped && (
          <Alert severity="info" sx={{ mt: 2 }}>{result.skipped}</Alert>
        )}
        {result && (
          <Box sx={{ mt: 2 }}>
            <IntelResultList lookup={result} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default IndicatorLookup;
